/**
 * Fiat display currencies — the SINGLE source of truth for both apps (#134).
 *
 * Before this module the list lived in TWO hand-maintained places: the picker in
 * `apps/web/src/components/FiatCurrencyModal/index.tsx` and `VS_CURRENCIES` in
 * `apps/api/src/services/fiat-prices.ts`, whose comment ("All 24 non-USD currencies matching the
 * frontend CURRENCIES array") stated the coupling instead of enforcing it.
 *
 * Why that mattered — the drift failure mode is a SILENT money-display bug. If the picker offers a
 * currency the backend does not fetch:
 *   1. `useFiatPrices.ts` → `rate = rates[code] ?? null` → null
 *   2. `formatFiatAmount()` returns null
 *   3. the caller falls back to `${fiatSymbol}${usdAmount}` (e.g. SwapCard/index.tsx)
 * …so the SELECTED currency's symbol is printed against an UNCONVERTED USD figure — "RM2.48" for
 * $2.48, ~4.1× understated, with no error anywhere. Deriving both consumers from this array makes
 * that divergence structurally impossible rather than merely absent.
 *
 * Rates are USDC→fiat from CoinGecko `simple/price?ids=usd-coin&vs_currencies=…`. Every code below
 * was verified to return a live rate in a single call on 2026-07-18 — provider support is confirmed,
 * not assumed. Do not add a code without checking it there first; an unsupported code is exactly the
 * drift this module exists to prevent, just sourced upstream.
 */

export interface FiatCurrency {
  /** ISO 4217 code, UPPERCASE. Lowercased for the CoinGecko query and the rates map. */
  code: string;
  name: string;
  /** Display symbol used ONLY as a fallback prefix; `Intl.NumberFormat` formats the real thing. */
  symbol: string;
}

/**
 * Currencies deliberately NOT offered, with the reason, so the omission is self-documenting and
 * survives someone later noticing CoinGecko supports them. See #134.
 *
 * Sanctions note: the exposure is narrower than it looks — CoinGecko offers no IRR, KPW, SYP, CUP or
 * BYN, so most sanctioned currencies are unavailable regardless of policy.
 */
export const EXCLUDED_FIAT_CURRENCIES: ReadonlyArray<{ code: string; reason: string }> = [
  {
    code: 'RUB',
    reason:
      'Russia is geo-blocked by our own Terms (§4, with Belarus/Cuba/Iran/North Korea/Syria and the ' +
      'Crimea, Donetsk and Luhansk regions). Offering the currency contradicts the access policy we publish.',
  },
  {
    code: 'MMK',
    reason:
      'Myanmar — subject to targeted sanctions. Not in our blocked-jurisdiction list, so displaying a ' +
      'price would not be a violation, but we do not advertise markets we will not serve.',
  },
  {
    code: 'VEF',
    reason:
      'Venezuela — sanctions, and the code is dead regardless: VEF was redenominated to VES in 2018 ' +
      'and CoinGecko does not offer VES, so there is no correct way to serve it here.',
  },
  {
    code: 'XDR',
    reason: 'IMF Special Drawing Rights — an accounting unit, not a currency anyone holds.',
  },
];

/**
 * USD is the base (USDC ≈ USD, no conversion) and MUST stay first — the UI treats index 0 as the
 * default and `useFiatPrices` short-circuits on it rather than reading a rate.
 */
export const FIAT_CURRENCIES: ReadonlyArray<FiatCurrency> = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'Fr' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$' },
  { code: 'TWD', name: 'Taiwan Dollar', symbol: 'NT$' },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr' },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$' },
  { code: 'MXN', name: 'Mexican Peso', symbol: '$' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
  { code: 'ARS', name: 'Argentine Peso', symbol: '$' },
  { code: 'CLP', name: 'Chilean Peso', symbol: '$' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺' },
  { code: 'PLN', name: 'Polish Złoty', symbol: 'zł' },
  { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč' },
  { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft' },
  { code: 'UAH', name: 'Ukrainian Hryvnia', symbol: '₴' },
  { code: 'GEL', name: 'Georgian Lari', symbol: '₾' },
  { code: 'ILS', name: 'Israeli Shekel', symbol: '₪' },
  { code: 'THB', name: 'Thai Baht', symbol: '฿' },
  { code: 'VND', name: 'Vietnamese Dong', symbol: '₫' },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM' },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp' },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱' },
  { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨' },
  { code: 'BDT', name: 'Bangladeshi Taka', symbol: '৳' },
  { code: 'LKR', name: 'Sri Lankan Rupee', symbol: 'Rs' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
  { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼' },
  { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'د.ك' },
  { code: 'BHD', name: 'Bahraini Dinar', symbol: '.د.ب' },
  { code: 'BMD', name: 'Bermudian Dollar', symbol: '$' },
];

/** The base currency needing no conversion. Kept as a constant so consumers stop hardcoding 'usd'. */
export const BASE_FIAT_CURRENCY = 'USD';

/**
 * Comma-separated LOWERCASE codes for the CoinGecko `vs_currencies` query, USD excluded (it is the
 * base — requesting it would be a wasted field). Derived, never hand-written: this is the half that
 * used to drift out of sync with the picker.
 */
export const FIAT_VS_CURRENCIES: string = FIAT_CURRENCIES
  .filter(c => c.code !== BASE_FIAT_CURRENCY)
  .map(c => c.code.toLowerCase())
  .join(',');

/** Lookup by code, case-insensitive. Returns undefined for anything not offered. */
export function getFiatCurrency(code: string): FiatCurrency | undefined {
  const upper = code.toUpperCase();
  return FIAT_CURRENCIES.find(c => c.code === upper);
}

/** Whether a code is one we offer (and therefore one we fetch a rate for). */
export function isSupportedFiatCurrency(code: string): boolean {
  return getFiatCurrency(code) !== undefined;
}
